import { useLocation, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { AlertMessage } from "../components/AlertMessage";
import { CircularProgress, Typography, Box } from "@mui/material";

const RequireAuth = ({ menuId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [unauthorized, setUnauthorized] = useState(false);

  const childRef = useRef();

  const user = JSON.parse(localStorage.getItem("user"));

  const allowedMenus = user?.menus
    ? user.menus.split(",").map((id) => parseInt(id.trim(), 10))
    : [];
  const requiredMenuIds = Array.isArray(menuId) ? menuId : [menuId];

  useEffect(() => {
    // No logged-in user at all isn't a "wrong menu permission" case — it's
    // handled by the !user redirect-to-login below. Without this guard,
    // allowedMenus is always [] for a logged-out visit, so this effect ran
    // anyway, fired an "unauthorized" toast, and scheduled navigate(-1) —
    // racing the login redirect and, since the timeout was never cleared,
    // firing 5s later on whatever page came next (looked like the page
    // "vanished" after a few seconds when hitting a deep link logged out).
    if (!user) return;

    // Chief Executive can open every page; other roles follow their position's
    // menus (Manage Roles & Positions). role_id isn't stable across
    // environments (CE's role_id differs in live), so match position_name —
    // mirroring the sidebar's CE check — with 377 kept as a fallback.
    const ceName = (user.position_name || "").trim().toLowerCase();
    if (ceName === "chief executive" || Number(user.role_id) === 377) return;

    // Menu ID 1 is Dashboard — always accessible for any logged-in user.
    if (
      requiredMenuIds.length > 0 &&
      !requiredMenuIds.includes(1) &&
      !requiredMenuIds.some((requiredId) => allowedMenus.includes(requiredId))
    ) {
      childRef.current?.triggerAlert(
        JSON.stringify({
          message: "Sorry!, You are unauthorized to view this page",
          messageType: "error",
        })
      );
      setUnauthorized(true);

      const timer = setTimeout(() => {
        navigate(-1);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [user, menuId, requiredMenuIds, allowedMenus, navigate]);

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (unauthorized) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <AlertMessage ref={childRef} />
        <CircularProgress sx={{ mt: 3 }} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Redirecting to the previous page...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Outlet />
      <AlertMessage ref={childRef} />
    </>
  );
};

export default RequireAuth;
